import { useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * Permission codes — must match `permission.permission_code` in DB.
 * Keep in sync with supabase/migrations/004_seed_master_data.sql.
 */
export const PERM = {
  // Master Data — Vehicle / Parent SKU
  VEHICLE_VIEW: 'vehicle.view',
  VEHICLE_CREATE_INLINE: 'vehicle.create_inline',
  VEHICLE_EDIT: 'vehicle.edit',

  // Master Data — Part / Child SKU
  PART_VIEW: 'part.view',
  PART_CREATE_INLINE: 'part.create_inline',
  PART_EDIT: 'part.edit',

  // Master Data — Other
  HSN_VIEW: 'hsn.view',
  HSN_EDIT: 'hsn.edit',

  // Customer
  CUSTOMER_VIEW: 'customer.view',
  CUSTOMER_CREATE: 'customer.create',
  CUSTOMER_EDIT: 'customer.edit',
  CUSTOMER_VIEW_HISTORY: 'customer.view_history',

  // Inventory
  INVENTORY_VIEW_STOCK: 'inventory.view_stock',
  INBOUND_CREATE: 'inbound.create',
  INBOUND_EDIT: 'inbound.edit',
  INVENTORY_ADJUST: 'inventory.adjust',
  INVENTORY_WRITE_OFF: 'inventory.write_off',

  // Sales / Cart
  CART_CREATE: 'cart.create',
  CART_EDIT_LINE: 'cart.edit_line',
  CART_APPLY_STANDARD_PRICE: 'cart.apply_standard_price',
  CART_APPLY_DISCOUNT_LOW: 'cart.apply_discount_low',
  CART_APPLY_DISCOUNT_HIGH: 'cart.apply_discount_high',
  CART_OVERRIDE_PRICE: 'cart.override_price',
  CART_SEND_TO_CASHIER: 'cart.send_to_cashier',
  CART_CANCEL_PROFORMA: 'cart.cancel_proforma',

  // Cashier
  PAYMENT_VIEW_PENDING: 'payment.view_pending',
  PAYMENT_MARK_RECEIVED: 'payment.mark_received',
  PAYMENT_CAPTURE_REFERENCE: 'payment.capture_reference',
  PAYMENT_REVERSE: 'payment.reverse',
  CASHIER_EOD_SUBMIT: 'cashier.eod_submit',
  CASHIER_PETTY_CASH_ISSUE: 'cashier.petty_cash_issue',
  CASHIER_CASH_RECEIPT_ACCEPT: 'cashier.cash_receipt_accept',

  // Approvals
  APPROVAL_VIEW_QUEUE: 'approval.view_queue',
  APPROVAL_DECIDE: 'approval.decide',
  APPROVAL_COFOUNDER_DECIDE: 'approval.cofounder_decide',

  // Reports
  REPORT_DAILY_SALES: 'report.daily_sales',
  REPORT_INVENTORY_VS_SALES: 'report.inventory_vs_sales',
  REPORT_PARTS_SALES: 'report.parts_sales',
  REPORT_GST_SUMMARY: 'report.gst_summary',
  REPORT_CASHIER_EOD: 'report.cashier_eod',
  REPORT_EXPORT_CSV: 'report.export_csv',
  REPORT_DOWNLOAD_ALL_DATA: 'report.download_all_data',

  // Settings
  SETTINGS_BUSINESS_PROFILE: 'settings.business_profile',
  SETTINGS_USERS_VIEW: 'settings.users_view',
  SETTINGS_USERS_MANAGE: 'settings.users_manage',
  SETTINGS_PERMISSIONS_ASSIGN: 'settings.permissions_assign',
  SETTINGS_PRICING_RULES: 'settings.pricing_rules',
  SETTINGS_APPROVAL_THRESHOLDS: 'settings.approval_thresholds',

  // Audit
  AUDIT_VIEW_LOG: 'audit.view_log',
  AUDIT_EXPORT: 'audit.export',

  // Compliance / DPDP (v0.2.3)
  DSR_VIEW_INBOX: 'dsr.view_inbox',
  DSR_FULFILL: 'dsr.fulfill',
  CUSTOMER_EXPORT_PII: 'customer.export_pii',
  CUSTOMER_ERASE_PII: 'customer.erase_pii',
} as const;

export type PermissionCode = (typeof PERM)[keyof typeof PERM];

/**
 * v0.2.24 — Permission cache with circuit breaker.
 * Caches both successful AND failed fetches to prevent React render loops from
 * hammering the DB. Without this, when my_permissions RPC fails (e.g. CPU
 * saturation, RLS misconfig, network hiccup), every component using
 * useHasPermission re-fires the RPC on every render — easily 100+ req/sec
 * per browser tab. That's how the prod DB accumulated 169M requests in 3 days.
 *
 * New behavior:
 *   - One in-flight fetch promise — multiple components await the same promise
 *   - On success: cache for the session
 *   - On failure: cache the failure with exponential backoff (1s → 30s max)
 *   - After 5 consecutive failures: lock out for 5 min, require manual reset
 */
let cache: Set<string> | null = null;
let inflight: Promise<Set<string>> | null = null;
let failureCount = 0;
let nextRetryAt = 0;
const MAX_BACKOFF_MS = 30_000;
const MAX_FAILURES = 5;
const LOCKOUT_MS = 5 * 60_000;

function nextBackoff(): number {
  if (failureCount >= MAX_FAILURES) return LOCKOUT_MS;
  return Math.min(1000 * Math.pow(2, failureCount), MAX_BACKOFF_MS);
}

export async function loadMyPermissions(): Promise<Set<string>> {
  if (cache) return cache;
  if (inflight) return inflight;
  if (Date.now() < nextRetryAt) {
    // Circuit open — return empty Set without firing RPC
    return new Set();
  }
  inflight = (async () => {
    try {
      const { data, error } = await supabase.rpc('my_permissions');
      if (error) throw error;
      const perms = new Set((data as { permission_code: string }[]).map((r) => r.permission_code));
      cache = perms;
      failureCount = 0;
      nextRetryAt = 0;
      return perms;
    } catch (e) {
      failureCount += 1;
      const backoff = nextBackoff();
      nextRetryAt = Date.now() + backoff;
      console.warn(`Permission fetch failed (attempt ${failureCount}/${MAX_FAILURES}). Backing off ${backoff}ms.`, e);
      return new Set();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function clearPermissionCache() {
  cache = null;
  inflight = null;
  failureCount = 0;
  nextRetryAt = 0;
}

/** React hook — returns whether the current user holds the given permission(s). */
export function useHasPermission(code: PermissionCode | PermissionCode[]): boolean {
  const codes = Array.isArray(code) ? code : [code];
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadMyPermissions().then((perms) => {
      if (!cancelled) setGranted(codes.every((c) => perms.has(c)));
    });
    return () => { cancelled = true; };
  }, [codes.join('|')]);

  return granted;
}
